import Box from '@mui/material/Box'
import { common } from '@mui/material/colors'
import { styled } from '@mui/material/styles'
import mapboxgl from 'mapbox-gl'
import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import CanceledMarker from 'assets/map/CanceledMarker.png'
import ContainerMarker from 'assets/map/ContainerMarker.png'
import HoldMarker from 'assets/map/HoldMarker.png'
import InProgressMarker from 'assets/map/InProgressMarker.png'
import ScheduledMarker from 'assets/map/ScheduledMarker.png'
import ServicedMarker from 'assets/map/ServicedMarker.png'
import SkippedMarker from 'assets/map/SkippedMarker.png'
import ToBeInsertedMarker from 'assets/map/ToBeInsertedMarker.png'
import Constants from 'Constants'
import { MAP_DEFAULT_OPTIONS } from 'settings/constants/map'
import { addAssetsImageToMap, addSourceAsync } from 'utils/map'

const { MEDIA_SERVER } = Constants
const { LIGHT_VIEW, MAP_VIEW_URL, SATELLITE_STREET_VIEW } = MAP_DEFAULT_OPTIONS

const StyledImageButton = styled('img')({
  height: 70,
  cursor: 'pointer',
})

type MarkerFeature = { geometry: { coordinates: number[] }; properties?: Record<string, unknown> }

type Props = {
  latitude?: number | string | null
  longitude?: number | string | null
  features?: MarkerFeature[]
}

type AuthSliceState = { AuthReducer?: { userInfo?: { mapBoxToken?: string } } }

const stopPropagation = (event: React.MouseEvent) => event.stopPropagation()

const WorkOrderLocationMap: React.FC<Props> = ({ latitude, longitude, features = [] }) => {
  const mapContainer = useRef<HTMLDivElement | null>(null)
  const map = useRef<ReturnType<typeof mapboxgl.Map> | null>(null)
  const hasInitializedRef = useRef(false)
  const [isStreetView, setIsStreetView] = useState(true)
  const userInfo = useSelector((s: AuthSliceState) => s.AuthReducer?.userInfo)
  mapboxgl.accessToken = userInfo?.mapBoxToken

  const loadMapContent = (shouldFlyTo = false) => {
    if (!map.current) return

    const markerCoordinates = latitude && longitude && [longitude, latitude]

    // Add marker images
    addAssetsImageToMap(map.current, 'canceled-marker', CanceledMarker)
    addAssetsImageToMap(map.current, 'hold-marker', HoldMarker)
    addAssetsImageToMap(map.current, 'in-progress-marker', InProgressMarker)
    addAssetsImageToMap(map.current, 'scheduled-marker', ScheduledMarker)
    addAssetsImageToMap(map.current, 'serviced-marker', ServicedMarker)
    addAssetsImageToMap(map.current, 'skipped-marker', SkippedMarker)
    addAssetsImageToMap(map.current, 'to-be-assigned-marker', ToBeInsertedMarker)
    addAssetsImageToMap(map.current, 'container-marker', ContainerMarker)

    // Add marker source and layers
    addSourceAsync(map.current, 'marker-source', { type: 'FeatureCollection', features }, { type: 'geojson' }).then(() => {
      if (!map.current.getLayer('marker-layer')) {
        map.current.addLayer({
          id: 'marker-layer',
          source: 'marker-source',
          type: 'symbol',
          layout: {
            'icon-image': ['get', 'icon'],
          },
        })
      }

      if (!map.current.getLayer('position-layer')) {
        map.current.addLayer({
          id: 'position-layer',
          type: 'symbol',
          source: 'marker-source',
          layout: {
            'icon-image': ['get', 'icon'],
            'text-field': ['get', 'position'],
            'text-anchor': 'top',
            'icon-offset': [0, -50],
            'text-offset': [0, -3.45],
            'icon-size': 0.55,
            'text-size': 12,
          },
          paint: {
            'text-color': common.black,
          },
        })
      }
    })

    // Only fly to location on initial load
    if (shouldFlyTo && markerCoordinates) {
      map.current.flyTo({
        center: markerCoordinates,
        zoom: 18,
        speed: 2,
        essential: false,
      })
    }
  }

  useEffect(() => {
    if (!map.current && mapContainer.current) {
      const initialCoords = latitude && longitude ? [longitude, latitude] : [-98.5795, 39.82835]
      const initialZoom = latitude && longitude ? 18 : 3.6

      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: `${MAP_VIEW_URL}${isStreetView ? LIGHT_VIEW : SATELLITE_STREET_VIEW}`,
        center: initialCoords,
        zoom: initialZoom,
      })

      map.current.on('load', () => {
        if (!hasInitializedRef.current) {
          hasInitializedRef.current = true
          loadMapContent(true) // Fly to location only on first load
        }
      })

      map.current.on('style.load', () => {
        // On style change, reload content but don't fly
        if (hasInitializedRef.current) {
          loadMapContent(false)
        }
      })

      map.current.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right')
    }

    return () => {
      if (map.current) {
        map.current.remove()
        map.current = null
        hasInitializedRef.current = false
      }
    }
  }, [])

  useEffect(() => {
    if (map.current && hasInitializedRef.current) {
      map.current.setStyle(`${MAP_VIEW_URL}${isStreetView ? LIGHT_VIEW : SATELLITE_STREET_VIEW}`)
    }
  }, [isStreetView])

  const toggleMapStyle = useCallback(() => setIsStreetView(prev => !prev), [])

  return (
    <Box height="250px" width="100%">
      <Box position="relative" onClick={stopPropagation}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" position="absolute" top={6} right={6} zIndex={1}>
          <StyledImageButton src={`${MEDIA_SERVER}${isStreetView ? 'SatelliteView.png' : 'StreetView.png'}`} onClick={toggleMapStyle} />
        </Box>
      </Box>
      <Box ref={mapContainer} height="250px" width="100%" />
    </Box>
  )
}

export default memo(WorkOrderLocationMap)
